import { useEffect, useState } from "react";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";
import Spade from "@/components/Spade";

const links = [
    { id: "about", label: "About", tid: NAV.linkAbout },
    { id: "framework", label: "Framework", tid: NAV.linkFramework },
    { id: "services-recap", label: "Services", tid: NAV.linkServices },
    { id: "work", label: "Work", tid: NAV.linkWork },
    { id: "testimonials", label: "Reviews", tid: NAV.linkTestimonials },
    { id: "team", label: "Team", tid: NAV.linkTeam },
];

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent("Hi, I want to book my $49 Aestherium Media trial week.");

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid={NAV.root}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[rgba(10,0,0,0.75)] border-b border-[var(--border-subtle)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
                <button
                    data-testid={NAV.logo}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="flex items-center gap-2 group"
                    aria-label="Aestherium Media"
                >
                    <Spade size={26} />
                    <span className="font-display text-xl md:text-2xl tracking-tight text-[var(--text-primary)] group-hover:text-[var(--accent-champagne)] transition-colors">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={l.tid}
                            onClick={() => scrollTo(l.id)}
                            className="text-[13px] uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--accent-champagne)] transition-colors relative group"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full bg-[var(--accent-gold)] transition-all duration-500" />
                        </button>
                    ))}
                    <a
                        data-testid={NAV.linkPortal}
                        href="/portal"
                        className="text-[13px] uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--accent-champagne)] transition-colors relative group"
                    >
                        Client Login
                        <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full bg-[var(--accent-gold)] transition-all duration-500" />
                    </a>
                </nav>

                <a
                    data-testid={NAV.ctaWhatsapp}
                    href={WA_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="hidden md:inline-flex items-center gap-2 px-4 md:px-5 py-2.5 text-xs md:text-sm uppercase tracking-[0.2em] bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold rounded-full hover:bg-[var(--accent-champagne)] transition-colors shadow-[0_10px_30px_-10px_rgba(212,175,55,0.55)]"
                >
                    Book $49 Trial
                </a>

                <button
                    data-testid={NAV.mobileToggle}
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 text-[var(--text-primary)]"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[var(--border-subtle)] bg-[rgba(10,0,0,0.95)] backdrop-blur-xl">
                    <div className="px-4 py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => scrollTo(l.id)}
                                className="text-left text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                            >
                                {l.label}
                            </button>
                        ))}
                        <a
                            data-testid={NAV.mobileLinkPortal}
                            href="/portal"
                            onClick={() => setOpen(false)}
                            className="text-left text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                        >
                            Client Login
                        </a>
                        <a
                            href={WA_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex justify-center items-center px-5 py-3 text-sm uppercase tracking-[0.2em] bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold rounded-full"
                        >
                            Book $49 Trial
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
